import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const TEASER_ITEMS = [
  { num: '01', domain: '医療', label: 'Healthcare' },
  { num: '02', domain: '福祉', label: 'Welfare' },
  { num: '03', domain: '教育', label: 'Education' },
  { num: '04', domain: '地域', label: 'Community' },
  { num: '05', domain: '文化', label: 'Culture' },
];

export default function CasesSectionComingSoon() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-28 md:py-40 relative overflow-hidden"
      style={{ backgroundColor: '#f5f3ef' }}
    >
      {/* Subtle background texture lines */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {[15, 35, 55, 75, 90].map((pct) => (
          <div
            key={pct}
            className="absolute top-0 bottom-0 w-px bg-divider/40"
            style={{ left: `${pct}%` }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">

        {/* Header */}
        <div
          className="mb-16 md:mb-24"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s',
          }}
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="block w-6 h-px bg-accent-teal" />
            <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Featured Cases</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl mb-3">
                変化の記録
              </h2>
              <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-md">
                医療・福祉・教育・地域・文化の現場から、代表的な事例をご紹介します。
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent-teal" style={{ animation: 'pulse 2s ease-in-out infinite' }} />
              <span className="font-sans-jp text-xs text-accent-teal tracking-widest-jp">準備中</span>
            </div>
          </div>
        </div>

        {/* Teaser grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-divider mb-3">
          {TEASER_ITEMS.map((item, i) => (
            <div
              key={item.num}
              className="bg-warm-white/80 relative overflow-hidden group"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(16px)',
                transition: `opacity 0.7s ease ${0.2 + i * 0.1}s, transform 0.7s ease ${0.2 + i * 0.1}s`,
              }}
            >
              {/* Shimmer sweep */}
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-accent-teal/6 to-transparent -translate-x-full pointer-events-none"
                style={{ animation: `shimmer 4s ${i * 0.7}s ease-in-out infinite` }}
                aria-hidden="true"
              />

              <div className="p-8 md:p-10 flex flex-col gap-6 min-h-[200px] md:min-h-[260px]">
                <div className="flex items-start justify-between">
                  <span className="font-garamond text-4xl font-light text-divider group-hover:text-accent-teal/40 transition-colors duration-500">
                    {item.num}
                  </span>
                  <span className="font-sans-jp text-[9px] text-muted-light tracking-widest-jp border border-divider px-2 py-0.5 mt-1">
                    {item.domain}
                  </span>
                </div>

                <div className="flex-1 flex flex-col justify-end">
                  <p className="font-garamond italic text-muted-light text-sm mb-3">{item.label}</p>
                  {/* Blurred placeholder lines */}
                  <div className="space-y-2">
                    <div className="h-px bg-divider w-full" />
                    <div className="h-px bg-divider w-3/4" />
                    <div className="h-px bg-divider w-1/2" />
                  </div>
                </div>

                {/* Coming soon badge */}
                <div className="flex items-center gap-2">
                  <span className="inline-block w-1 h-1 rounded-full bg-accent-teal/50" style={{ animation: `pulse 2s ${i * 0.3}s ease-in-out infinite` }} />
                  <span className="font-sans-jp text-[9px] text-muted-light tracking-widest-jp">Coming Soon</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Large teaser banner */}
        <div
          className="border border-divider bg-warm-white/60 p-10 md:p-16 flex flex-col md:flex-row items-center md:items-end justify-between gap-8 relative overflow-hidden"
          style={{
            opacity: inView ? 1 : 0,
            transition: 'opacity 0.9s ease 0.7s',
          }}
        >
          {/* Animated scan line */}
          <div
            className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent-teal/40 to-transparent pointer-events-none"
            style={{ animation: 'scanLine 4s ease-in-out infinite' }}
            aria-hidden="true"
          />

          <div>
            <p className="font-garamond italic text-muted-light text-base md:text-lg mb-3 tracking-wide">
              Documenting the moments of transformation.
            </p>
            <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-lg">
              それぞれの組織が社会と出会い直す瞬間を、丁寧に記録してきました。<br className="hidden md:block" />
              その物語を、まもなくここに届けます。
            </p>
          </div>

          <Link
            to="/cases"
            className="inline-flex items-center gap-2 px-8 py-4 border border-charcoal text-charcoal text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-all duration-300 whitespace-nowrap flex-shrink-0 cursor-pointer group"
          >
            公開をお待ちください
            <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
          </Link>
        </div>

        {/* Domain tags */}
        <div className="mt-10 flex items-center gap-3 flex-wrap"
          style={{
            opacity: inView ? 1 : 0,
            transition: 'opacity 0.9s ease 0.9s',
          }}
        >
          <span className="font-sans-jp text-xs text-muted-light tracking-widest-jp">支援領域：</span>
          {['医療', '福祉', '教育', '地域', '文化'].map((domain) => (
            <span
              key={domain}
              className="text-xs font-sans-jp text-muted-light border border-divider px-3 py-1 whitespace-nowrap opacity-50"
            >
              {domain}
            </span>
          ))}
        </div>

      </div>
    </section>
  );
}
